import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { FaFilePdf, FaTimes, FaSpinner, FaPrint, FaDownload } from 'react-icons/fa';

const API = '/api/v1/fsm';

const GenerateContractPDF = ({ contractId, contractNumber }) => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (showModal) fetchTemplates();
  }, [showModal]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/contract-templates`, { headers });
      const data = await res.json();
      const list = data?.data || data?.templates || [];
      setTemplates(list);
      if (list.length > 0 && !selectedTemplate) setSelectedTemplate(list[0].id);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
    setLoading(false);
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/contract-templates/${selectedTemplate}/generate/${contractId}`, { headers });
      if (!res.ok) throw new Error('Failed to generate');
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setShowModal(false);
      setShowPreview(true);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert(isRTL ? '\u0641\u0634\u0644 \u0641\u064a \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u0645\u0633\u062a\u0646\u062f' : 'Failed to generate document');
    }
    setGenerating(false);
  };

  const handlePrint = () => {
    const iframe = document.getElementById('contract-preview-iframe');
    if (iframe) iframe.contentWindow.print();
  };

  const handleOpenNewTab = () => {
    if (previewUrl) window.open(previewUrl, '_blank');
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setShowModal(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', backgroundColor: '#1a2038', color: 'white',
          border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer'
        }}
      >
        <FaFilePdf /> {isRTL ? '\u0625\u0646\u0634\u0627\u0621 PDF' : 'Generate PDF'}
      </button>

      {/* Template Selector Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#1a2035', borderRadius: 12, padding: 24, color: '#e0e0e0',
            width: 480, maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto',
            direction: isRTL ? 'rtl' : 'ltr', border: '1px solid #2d3748',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#e0e0e0' }}>
                {isRTL ? '\u0625\u0646\u0634\u0627\u0621 \u0639\u0642\u062f PDF' : 'Generate Contract PDF'}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af' }}>
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: 12, backgroundColor: '#111827', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#9ca3af', border: '1px solid #374151' }}>
              {isRTL ? '\u0627\u0644\u0639\u0642\u062f' : 'Contract'}: <strong style={{ color: '#e0e0e0' }}>{contractNumber}</strong>
            </div>

            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#d1d5db' }}>
              {isRTL ? '\u0627\u062e\u062a\u0631 \u0627\u0644\u0642\u0627\u0644\u0628' : 'Select Template'}
            </label>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>
                <FaSpinner className="fa-spin" /> {isRTL ? '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...' : 'Loading...'}
              </div>
            ) : templates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>
                {isRTL ? '\u0644\u0627 \u062a\u0648\u062c\u062f \u0642\u0648\u0627\u0644\u0628.' : 'No templates found.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {templates.map(tmpl => (
                  <div
                    key={tmpl.id}
                    onClick={() => setSelectedTemplate(tmpl.id)}
                    style={{
                      padding: '12px 16px',
                      border: `2px solid ${selectedTemplate === tmpl.id ? '#3b82f6' : '#374151'}`,
                      borderRadius: 8, cursor: 'pointer',
                      backgroundColor: selectedTemplate === tmpl.id ? '#1e3a5f' : '#111827',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#e0e0e0' }}>{tmpl.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      {tmpl.language === 'ar' ? '\ud83c\uddea\ud83c\uddec Arabic' : '\ud83c\uddec\ud83c\udde7 English'}
                      {tmpl.is_default ? (isRTL ? ' \u2022 \u0627\u0641\u062a\u0631\u0627\u0636\u064a' : ' \u2022 Default') : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{
                padding: '10px 20px', border: '1px solid #374151', borderRadius: 6,
                background: '#111827', color: '#d1d5db', cursor: 'pointer', fontSize: 13
              }}>
                {isRTL ? '\u0625\u0644\u063a\u0627\u0621' : 'Cancel'}
              </button>
              <button
                onClick={handleGenerate}
                disabled={!selectedTemplate || generating}
                style={{
                  padding: '10px 20px', border: 'none', borderRadius: 6,
                  background: selectedTemplate && !generating ? '#3b82f6' : '#374151',
                  color: 'white', cursor: selectedTemplate && !generating ? 'pointer' : 'not-allowed',
                  fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {generating
                  ? (<><FaSpinner className="fa-spin" /> {isRTL ? '\u062c\u0627\u0631\u064a...' : 'Generating...'}</>)
                  : (<><FaFilePdf /> {isRTL ? '\u0625\u0646\u0634\u0627\u0621 \u0648\u0645\u0639\u0627\u064a\u0646\u0629' : 'Generate & Preview'}</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen Preview Overlay */}
      {showPreview && previewUrl && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: '#0f1219', zIndex: 10000,
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Toolbar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 20px', backgroundColor: '#1a2035', borderBottom: '1px solid #2d3748',
          }}>
            <div style={{ color: '#e0e0e0', fontWeight: 600, fontSize: 15 }}>
              {isRTL ? '\u0645\u0639\u0627\u064a\u0646\u0629 \u0627\u0644\u0639\u0642\u062f' : 'Contract Preview'}: {contractNumber}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handlePrint} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', background: '#3b82f6', color: 'white',
                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              }}>
                <FaPrint /> {isRTL ? '\u0637\u0628\u0627\u0639\u0629' : 'Print'}
              </button>
              <button onClick={handleOpenNewTab} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', background: '#111827', color: '#d1d5db',
                border: '1px solid #374151', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              }}>
                <FaDownload /> {isRTL ? '\u0641\u062a\u062d \u0641\u064a \u062a\u0627\u0628 \u062c\u062f\u064a\u062f' : 'Open in New Tab'}
              </button>
              <button onClick={() => { setShowPreview(false); }} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', background: '#dc2626', color: 'white',
                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              }}>
                <FaTimes /> {isRTL ? '\u0625\u063a\u0644\u0627\u0642' : 'Close'}
              </button>
            </div>
          </div>

          {/* iframe Preview */}
          <iframe
            id="contract-preview-iframe"
            src={previewUrl}
            title="Contract Preview"
            style={{
              flex: 1, border: 'none', width: '100%',
              backgroundColor: '#e0e0e0',
            }}
          />
        </div>
      )}
    </>
  );
};

export default GenerateContractPDF;
